import {collection,doc,getDoc,getDocs,query,where,orderBy,limit,startAfter,runTransaction,serverTimestamp} from 'firebase/firestore';
import {db} from '../../firebase';
import {getSalesContext} from './salesService';
import {entityPayload,ENTITY_FIELDS} from './accountModel';
import {leadPayload} from './salesModel';
export async function checkedContext(expected) {
 const current=await getSalesContext();
 if(current.uid!==expected.uid || current.tenantId!==expected.tenantId) throw new Error('Sales access changed. Reopen Sales.');
 return current;
}
export async function readEntities(kind,expected) {
 if(!ENTITY_FIELDS[kind]) throw new Error('Unsupported Sales record.');
 const rows=[];let cursor;
 do {
  const context=await checkedContext(expected);
  const snap=await getDocs(query(collection(db,kind),where('tenantId','==',context.tenantId),orderBy('__name__'),...(cursor?[startAfter(cursor)]:[]),limit(100)));
  rows.push(...snap.docs.filter(d=>d.data().tenantId===context.tenantId).map(d=>({...d.data(),id:d.id})));
  if(snap.size<100) break;cursor=snap.docs.at(-1);
 }while(cursor);
 await checkedContext(expected);return rows;
}
async function requireParent(tx,kind,id,tenant,accountId) {
 if(!id) throw new Error('Parent reference is required.');
 const snap=await tx.get(doc(db,kind,id));
 if(!snap.exists() || snap.data().tenantId!==tenant || (accountId && snap.data().accountId!==accountId)) throw new Error('Reference must belong to this tenant and account.');
 return snap.data();
}
const audit=(context,isNew)=>({tenantId:context.tenantId,updatedBy:context.uid,updatedAt:serverTimestamp(),...(isNew?{createdBy:context.uid,createdAt:serverTimestamp()}:{})});
export async function saveEntity(kind,draft,expected) {
 const context=await checkedContext(expected),data=entityPayload(kind,draft);
 const ref=draft.id?doc(db,kind,draft.id):doc(collection(db,kind));
 await runTransaction(db,async tx=>{
  if(draft.id){const old=await requireParent(tx,kind,draft.id,context.tenantId);if(kind!=='salesAccounts' && old.accountId!==data.accountId) throw new Error('Parent account cannot be changed.');}
  if(kind!=='salesAccounts') await requireParent(tx,'salesAccounts',data.accountId,context.tenantId);
  if(kind==='salesContacts' && data.locationId) await requireParent(tx,'salesLocations',data.locationId,context.tenantId,data.accountId);
  if(kind==='salesAccounts' && data.primaryContactId) await requireParent(tx,'salesContacts',data.primaryContactId,context.tenantId,ref.id);
  if(kind==='salesLocations' && !draft.id) Object.assign(data,{operationalFacilityId:null,linkedAt:null,linkedBy:null});
  if(draft.id) tx.update(ref,{...data,...audit(context,false)});else tx.set(ref,{...data,...audit(context,true)});
 });return ref.id;
}
export async function convertLead(leadId,options,expected) {
 const context=await checkedContext(expected);
 // Generated once per invocation; all writes happen only after the lead is read in the transaction.
 const leadRef=doc(db,'salesLeads',leadId);
 const accountRef=options.accountId?doc(db,'salesAccounts',options.accountId):doc(collection(db,'salesAccounts'));
 const locationRef=options.location?doc(collection(db,'salesLocations')):null;
 const contactRef=options.contact?doc(collection(db,'salesContacts')):null;
 try { return await runTransaction(db,async tx=>{
  const lead=await requireParent(tx,'salesLeads',leadId,context.tenantId);
  if(lead.accountId) return {accountId:lead.accountId,locationId:lead.locationId||'',alreadyConverted:true};
  if(options.accountId) await requireParent(tx,'salesAccounts',options.accountId,context.tenantId);
  const account=options.accountId?null:entityPayload('salesAccounts',{...options.account,ownerUid:options.account?.ownerUid??context.uid,primaryContactId:''});
  const location=locationRef?entityPayload('salesLocations',{...options.location,accountId:accountRef.id}):null;
  const contact=contactRef?entityPayload('salesContacts',{...options.contact,accountId:accountRef.id,locationId:locationRef?.id||''}):null;
  if(account) tx.set(accountRef,{...account,...audit(context,true)});
  if(location) tx.set(locationRef,{...location,...audit(context,true),operationalFacilityId:null,linkedAt:null,linkedBy:null});
  if(contact) tx.set(contactRef,{...contact,...audit(context,true)});
  tx.update(leadRef,{...leadPayload(lead),...audit(context,false),accountId:accountRef.id,locationId:locationRef?.id||'',convertedAt:serverTimestamp(),convertedBy:context.uid});
  return {accountId:accountRef.id,locationId:locationRef?.id||'',alreadyConverted:false};
 });
 } catch (error) {
  // A concurrent winner may make immutable-link rules reject our stale transaction.
  // Confirm the persisted outcome before treating that retry as complete.
  await checkedContext(expected);
  const current=await getDoc(leadRef);
  if(current.exists() && current.data().tenantId===context.tenantId && current.data().accountId) {
   return {accountId:current.data().accountId,locationId:current.data().locationId||'',alreadyConverted:true};
  }
  throw error;
 }
}
